/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Pure helpers for CMS spec version strings. Kept out of the hooks files so
 * modules that only need the normalization (e.g. useCmsSpecVersions) don't pull
 * in the Rules query hooks — and so tests that mock those hooks don't have to
 * re-export this pure function.
 */

/**
 * Canonicalize a CMS spec version to the stored "V<major>.<…>" form so callers that
 * pass "3.0.0" (state_config.tmsis_version) and those that pass "V3.0.0" (the
 * cms_spec_versions list) hit the same query cache key and send the same param —
 * avoiding duplicate requests for identical data. The backend also normalizes, so
 * this is purely a client-side cache/dedup concern.
 */
export function canonicalCmsSpecVersion(version?: string): string | undefined {
  const trimmed = (version ?? "").trim();
  if (!trimmed) return undefined;
  return `V${trimmed.replace(/^[Vv]/, "")}`.toUpperCase();
}

/**
 * Compare two CMS spec versions by release order. Returns <0, 0 or >0, so it can be passed
 * straight to `Array.prototype.sort`.
 *
 * Segments are compared NUMERICALLY, which is the whole point: as text "V4.10.0" sorts before
 * "V4.3.0" (at the first differing character "1" < "3"), so a string comparison would rank the
 * newer version as older. A ceiling built on that would quietly let through the one version it
 * was meant to exclude.
 *
 * A missing trailing segment is zero, so "V4", "V4.0" and "V4.0.0" are one release. Non-numeric
 * segments count as zero rather than NaN, which would make every comparison false and silently
 * report unequal versions as equal.
 */
export function compareCmsSpecVersions(a: string, b: string): number {
  const segments = (v: string): number[] =>
    (canonicalCmsSpecVersion(v) ?? "")
      .replace(/^V/, "")
      .split(".")
      .map((part) => {
        const n = Number.parseInt(part, 10);
        return Number.isNaN(n) ? 0 : n;
      });

  const left = segments(a);
  const right = segments(b);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
